import { request } from '@umijs/max';
import { ApiResponse } from '@/modules/types/api.types';
import {
  RegisterRequest,
  LoginRequest,
  AuthResponseData,
  RefreshTokenRequest,
  FakeCaptcha,
} from '@/modules/infrastructure/users/types/auth.types';
import { CurrentUser } from '@/modules/infrastructure/users/types/user.types';
import { normalizePermissionList } from '@/modules/common/permissions/permissionRegistry';
import { readPermissionsFromToken } from '@/modules/common/permissions/permissionSource';
import { displayUserName } from '@/modules/infrastructure/users/utils/displayUserName';

export const tokenStorage = {
  get: () => localStorage.getItem('access_token'),
  set: (token: string) => localStorage.setItem('access_token', token),
  remove: () => localStorage.removeItem('access_token'),
};

export const refreshTokenStorage = {
  get: () => localStorage.getItem('refresh_token'),
  set: (token: string) => localStorage.setItem('refresh_token', token),
  remove: () => localStorage.removeItem('refresh_token'),
};

export const userStorage = {
  get: () => localStorage.getItem('user'),
  set: (user: string) => localStorage.setItem('user', user),
  remove: () => localStorage.removeItem('user'),
};

export const emailStorage = {
  get: () => localStorage.getItem('email'),
  set: (email: string) => localStorage.setItem('email', email),
  remove: () => localStorage.removeItem('email'),
};

export const roleStorage = {
  get: () => localStorage.getItem('role'),
  set: (role: string) => localStorage.setItem('role', role),
  remove: () => localStorage.removeItem('role'),
};

function getAuthHeaders(): Record<string, string> {
  const token = tokenStorage.get();
  if (token) {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }
  return {
    'Content-Type': 'application/json',
  };
}

export async function getCurrentUserInfo(): Promise<CurrentUser | undefined> {
  const token = tokenStorage.get();

  if (!token) {
    return undefined;
  }

  try {
    const response = await request('/api/v1/infrastructure/users/me', {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response && response.success && response.data) {
      const userData = response.data;
      const name = displayUserName(userData.name);

      userStorage.set(name);
      emailStorage.set(userData.login);

      // Права позиции: сначала из тела /me, иначе из payload текущего токена.
      // Пустой список означает, что бэкенд их пока не отдаёт.
      const fromBody = normalizePermissionList(userData.permissions);
      const permissions = fromBody.length ? fromBody : readPermissionsFromToken(token);

      return {
        name,
        email: userData.login,
        userid: userData.id,
        permissions,
        avatar: '/user-photo.png',
      };
    }
    return undefined;
  } catch (error) {
    console.error('Failed to get current user info:', error);
    throw error;
  }
}

export async function register(
  body: RegisterRequest,
  options?: { [key: string]: any },
): Promise<ApiResponse<AuthResponseData>> {
  return request('/api/v1/infrastructure/auth/register', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function login(
  body: LoginRequest,
  options?: { [key: string]: any },
): Promise<ApiResponse<AuthResponseData>> {
  return request('/api/v1/infrastructure/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function refreshToken(
  body: RefreshTokenRequest,
  options?: { [key: string]: any },
): Promise<ApiResponse<AuthResponseData>> {
  return request('/api/v1/infrastructure/auth/refresh', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

export async function logout(options?: { [key: string]: any }): Promise<ApiResponse<any>> {
  const authHeaders = getAuthHeaders();
  return request('/api/v1/infrastructure/auth/logout', {
    method: 'GET',
    headers: {
      ...authHeaders,
    },
    ...(options || {}),
  });
}

export async function getCaptcha(
  params: {
    phone?: string;
  },
  options?: { [key: string]: any },
) {
  return request<FakeCaptcha>('/api/login/captcha', {
    method: 'GET',
    params: {
      ...params,
    },
    ...(options || {}),
  });
}

export { register as signup };
/** @generated sync-swagger — Get Users */
export async function getusersapi(options?: { [key: string]: any }): Promise<unknown> {
  return request<unknown>(`/api/v1/infrastructure/users/`, {
    method: 'GET',
    headers: getAuthHeaders(),
    ...(options || {}),
  });
}
/** @generated sync-swagger — Get Current User Info */
export async function getcurrentuser(options?: { [key: string]: any }): Promise<unknown> {
  return request<unknown>(`/api/v1/infrastructure/users/me`, {
    method: 'GET',
    headers: getAuthHeaders(),
    ...(options || {}),
  });
}
/** @generated sync-swagger — Get User By Id */
export async function getuserby(options?: { [key: string]: any }): Promise<unknown> {
  return request<unknown>(`/api/v1/infrastructure/users/{user_id}`, {
    method: 'GET',
    headers: getAuthHeaders(),
    ...(options || {}),
  });
}
/** @generated sync-swagger — Update User */
export async function updateuserapi(options?: { [key: string]: any }): Promise<unknown> {
  return request<unknown>(`/api/v1/infrastructure/users/{user_id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    ...(options || {}),
  });
}
/** @generated sync-swagger — Delete User */
export async function deleteuserapi(options?: { [key: string]: any }): Promise<unknown> {
  return request<unknown>(`/api/v1/infrastructure/users/{user_id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    ...(options || {}),
  });
}
